
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { TerraceSubmission, DetailedOpeningHours } from "@/types/terrace";
import { toast } from "sonner";

type StatusFilterType = 'all' | 'pending' | 'approved' | 'rejected';

export const useSubmissions = (statusFilter: StatusFilterType) => {
  const [submissions, setSubmissions] = useState<TerraceSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSubmissions = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from("terrace_submissions")
        .select('*');
      
      // Apply status filter if not "all"
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      const { data, error } = await query.order('submitted_at', { ascending: false });

      if (error) {
        console.error("Error fetching submissions:", error);
        toast.error("Failed to fetch submissions");
      } else if (data) {
        // Transform database fields to match TerraceSubmission interface
        const transformedData: TerraceSubmission[] = data.map(submission => {
          // Parse detailed opening hours if it exists
          let detailedOpeningHours: DetailedOpeningHours | string | undefined;
          
          if (submission.detailed_opening_hours) {
            try {
              detailedOpeningHours = typeof submission.detailed_opening_hours === 'string' 
                ? JSON.parse(submission.detailed_opening_hours)
                : submission.detailed_opening_hours as unknown as DetailedOpeningHours;
            } catch (e) {
              console.warn("Failed to parse detailed opening hours:", e);
              detailedOpeningHours = undefined;
            }
          }
            
          return {
            id: submission.id,
            name: submission.name,
            address: submission.address,
            lat: submission.lat,
            lng: submission.lng,
            imageUrl: submission.imageurl,
            sunStatus: submission.sunstatus as 'sunny' | 'soon' | 'not-sunny',
            openingHours: submission.openinghours,
            detailed_opening_hours: detailedOpeningHours,
            orientation: submission.orientation,
            hasFood: submission.hasfood,
            petFriendly: submission.petfriendly,
            servesAlcohol: submission.servesalcohol,
            terraceDescription: submission.terracedescription,
            website: submission.website,
            phone: submission.phone,
            type: submission.type,
            status: submission.status as 'pending' | 'approved' | 'rejected',
            submitted_by: submission.submitter_name || submission.submitted_by,
            submitted_at: submission.submitted_at,
            submitter_name: submission.submitter_name,
            // Map the new API-related fields
            api_submission: submission.api_submission,
            auto_approved: submission.auto_approved,
            confidence_score: submission.confidence_score,
            // Map the place/map fields
            google_place_id: submission.google_place_id,
            place_name: submission.place_name,
            photo_url: submission.photo_url,
            original_maps_url: submission.original_maps_url, // <-- Add this to pass the value through
          };
        });
        
        setSubmissions(transformedData);
      }
    } catch (error) {
      console.error("Fetch submissions error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [statusFilter]);

  return { submissions, isLoading, fetchSubmissions };
};
